import styled from "styled-components";
import { largeComponentHeight } from "common/const/const";
import React, { useRef, useEffect } from "react";

import {
  inputBorderStyle,
  InputWrapper,
} from "common/components/Wrapper/InputBase";
import { useValidCheckSingle } from "common/hooks/validCheck";

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

const TimeInputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: ${largeComponentHeight};
  ${inputBorderStyle}
  padding-left: ${(props) =>
    props.iconPosition === "left" ? "3.5rem" : "1.25rem"};
  padding-right: ${(props) =>
    props.iconPosition === "right" ? "3.5rem" : "1.25rem"};

  &:read-only {
    background-color: var(--White);
  }

  ${({ $disabled }) =>
    $disabled &&
    `
    background-color: var(--LightGray) !important;
    cursor: not-allowed;
  `}
`;

const TimeSegmentInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-family);
  font-size: var(--fontSize-14);
  text-align: center;
  width: ${(props) => props.width || "2ch"};
  padding: 0;

  &::placeholder {
    color: var(--LightNavy);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Divider = styled.span`
  font-family: var(--font-family);
  font-size: var(--fontSize-14);
  user-select: none;
`;

const getHours = (timeString) => {
  if (!timeString) return null;
  const [hours] = timeString.split(":");
  return Number(hours);
};

const getMinutes = (timeString) => {
  if (!timeString) return null;
  const [, minutes] = timeString.split(":");
  return Number(minutes);
};

const safeMin = (...values) => {
  const filtered = values.filter((v) => v != null);
  return filtered.length ? Math.min(...filtered) : null;
};

const safeMax = (...values) => {
  const filtered = values.filter((v) => v != null);
  return filtered.length ? Math.max(...filtered) : null;
};

export const TimeInput = ({
  label,
  caption,
  icon,
  rules,
  name,
  disabled,
  readOnly,
  defaultValue,
  onChange,
  forceError,
  minTime,
  maxTime,
  ...rest
}) => {
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const lastFocusedRef = useRef(null);

  const { inputProps, error, onValueChange } = useValidCheckSingle({
    name,
    rules: {
      ...rules,
      validate: {
        ...rules?.validate,
        completeTime: (value) => {
          if (!value) return !rules?.required;
          return TIME_PATTERN.test(value);
        },
      },
    },
    defaultValue,
  });

  const hasError = forceError || error;

  const maxHour = safeMin(23, maxTime && getHours(maxTime));
  const minHour = safeMax(0, minTime && getHours(minTime));

  // form 값이 외부에서 변경될 때 DOM 동기화
  useEffect(() => {
    const val = inputProps.value ?? "";
    const [h, m] = (val || ":").split(":");
    if (hourRef.current && hourRef.current !== document.activeElement) {
      hourRef.current.value = disabled ? "" : h || "";
    }
    if (minuteRef.current && minuteRef.current !== document.activeElement) {
      minuteRef.current.value = disabled ? "" : m || "";
    }
  }, [inputProps.value, disabled]);

  const getMaxMinute = () => {
    const currentHour = Number(hourRef.current?.value) || 0;
    return maxTime && currentHour === getHours(maxTime)
      ? getMinutes(maxTime)
      : 59;
  };

  const getMinMinute = () => {
    const currentHour = Number(hourRef.current?.value) || 0;
    return minTime && currentHour === getHours(minTime)
      ? getMinutes(minTime)
      : 0;
  };

  const buildValue = () => {
    const h = hourRef.current?.value || "";
    const m = minuteRef.current?.value || "";
    if (!h && !m) return "";
    if (!h || !m) return `${h}:${m}`;
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  };

  const handleWrapperClick = (e) => {
    if (e.target === e.currentTarget) {
      if (lastFocusedRef.current === hourRef.current) {
        minuteRef.current?.focus();
      } else {
        hourRef.current?.focus();
      }
    }
  };

  const handleFocus = (e) => {
    e.target.select();
    lastFocusedRef.current = e.target;
  };

  const handleBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      const h = hourRef.current?.value || "";
      const m = minuteRef.current?.value || "";

      if (h) hourRef.current.value = h.padStart(2, "0");
      if (m) minuteRef.current.value = m.padStart(2, "0");

      const newValue = buildValue();
      onValueChange(newValue);
      onChange?.(newValue);
      inputProps.onBlur();
    }
  };

  const handleHourChange = (e) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > 2) val = val.slice(0, 2);

    if (val) {
      const numVal = Number(val);
      if (maxHour != null && numVal > maxHour) val = String(maxHour);
      if (minHour != null && numVal < minHour) val = String(minHour);
    }

    hourRef.current.value = val;

    if (val.length === 2) {
      minuteRef.current?.focus();
    }

    const newValue = buildValue();
    onValueChange(newValue);
    onChange?.(newValue);
  };

  const handleMinuteChange = (e) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > 2) val = val.slice(0, 2);

    if (val) {
      const numVal = Number(val);
      const max = getMaxMinute();
      const min = getMinMinute();
      if (numVal > max) val = String(max);
      if (numVal < min) val = String(min);
    }

    minuteRef.current.value = val;

    const newValue = buildValue();
    onValueChange(newValue);
    onChange?.(newValue);
  };

  const handleKeyDown = (e, isHour) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const delta = e.key === "ArrowUp" ? 1 : -1;

      if (isHour) {
        const current = Number(hourRef.current.value) || 0;
        const min = minHour ?? 0;
        const max = maxHour ?? 23;
        const next = Math.min(max, Math.max(min, current + delta));
        hourRef.current.value = String(next).padStart(2, "0");
        if (!minuteRef.current.value) minuteRef.current.value = "00";
      } else {
        const current = Number(minuteRef.current.value) || 0;
        const max = getMaxMinute();
        const min = getMinMinute();
        const next = Math.min(max, Math.max(min, current + delta));
        minuteRef.current.value = String(next).padStart(2, "0");
        if (!hourRef.current.value) hourRef.current.value = "00";
      }
      return;
    }

    if (e.key === "ArrowRight" && isHour) {
      e.preventDefault();
      minuteRef.current?.focus();
    } else if (e.key === "ArrowLeft" && !isHour) {
      e.preventDefault();
      hourRef.current?.focus();
    } else if (e.key === ":" && isHour) {
      e.preventDefault();
      minuteRef.current?.focus();
    }
  };

  return (
    <InputWrapper
      label={label}
      caption={caption}
      icon={icon}
      rules={rules}
      error={hasError}>
      <TimeInputWrapper
        iconPosition={icon?.position}
        $error={!!hasError}
        $disabled={disabled}
        onClick={handleWrapperClick}
        onBlur={handleBlur}>
        <TimeSegmentInput
          ref={hourRef}
          type="text"
          inputMode="numeric"
          defaultValue={defaultValue}
          placeholder="--"
          onFocus={handleFocus}
          onChange={handleHourChange}
          onKeyDown={(e) => handleKeyDown(e, true)}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={2}
          width="2.5ch"
        />
        <Divider>:</Divider>
        <TimeSegmentInput
          ref={minuteRef}
          type="text"
          inputMode="numeric"
          defaultValue={defaultValue}
          placeholder="--"
          onFocus={handleFocus}
          onChange={handleMinuteChange}
          onKeyDown={(e) => handleKeyDown(e, false)}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={2}
          width="2.5ch"
        />
      </TimeInputWrapper>
    </InputWrapper>
  );
};
